import { ApplicationConfig, ErrorHandler, isDevMode } from "@angular/core";
import { provideRouter, withComponentInputBinding } from "@angular/router";

import { provideEffects } from "@ngrx/effects";
import { provideStore } from "@ngrx/store";
import { provideStoreDevtools } from "@ngrx/store-devtools";

import { AppErrorHandler } from "./core/app-error.handler";
import { APP_ROUTES } from "./app.routes";
import { DataRepository } from "./core/data/data-repository";
import { LocalStorageRepository } from "./core/data/local-storage-repository";
import { CareerPathwaysEffects } from "./state/career-pathways/career-pathways.effects";
import { careerPathwaysFeature } from "./state/career-pathways/career-pathways.reducer";
import { ChecklistsEffects } from "./state/checklists/checklists.effects";
import { checklistsFeature } from "./state/checklists/checklists.reducer";
import { CompanyEffects } from "./state/company/company.effects";
import { companyFeature } from "./state/company/company.reducer";
import { SetupEffects } from "./state/setup/setup.effects";
import { setupFeature } from "./state/setup/setup.reducer";
import { DivisionsEffects } from "./state/divisions/divisions.effects";
import { FileDeletionsEffects } from "./state/file-deletions/file-deletions.effects";
import { fileDeletionsFeature } from "./state/file-deletions/file-deletions.reducer";
import { divisionsFeature } from "./state/divisions/divisions.reducer";
import { HandbookEffects } from "./state/handbook/handbook.effects";
import { handbookFeature } from "./state/handbook/handbook.reducer";
import { OnboardingEffects } from "./state/onboarding/onboarding.effects";
import { onboardingFeature } from "./state/onboarding/onboarding.reducer";
import { OpportunitiesEffects } from "./state/opportunities/opportunities.effects";
import { opportunitiesFeature } from "./state/opportunities/opportunities.reducer";
import { PacketEffects } from "./state/packet/packet.effects";
import { packetFeature } from "./state/packet/packet.reducer";
import { PeopleEffects } from "./state/people/people.effects";
import { peopleFeature } from "./state/people/people.reducer";
import { ProjectSettingsEffects } from "./state/project-settings/project-settings.effects";
import { projectSettingsFeature } from "./state/project-settings/project-settings.reducer";
import { ProjectsEffects } from "./state/projects/projects.effects";
import { projectsFeature } from "./state/projects/projects.reducer";
import { ReferenceEffects } from "./state/reference/reference.effects";
import { referenceFeature } from "./state/reference/reference.reducer";
import { RpmEffects } from "./state/rpm/rpm.effects";
import { rpmFeature } from "./state/rpm/rpm.reducer";
import { ScheduleEffects } from "./state/schedule/schedule.effects";
import { scheduleFeature } from "./state/schedule/schedule.reducer";
import { SubcontractorsEffects } from "./state/subcontractors/subcontractors.effects";
import { subcontractorsFeature } from "./state/subcontractors/subcontractors.reducer";
import { TimeOffEffects } from "./state/time-off/time-off.effects";
import { timeOffFeature } from "./state/time-off/time-off.reducer";
import { accountFeature } from "./state/account/account.reducer";
import { notificationsFeature } from "./state/notifications/notifications.reducer";
import { sessionFeature } from "./state/session/session.reducer";
import { TrainingEffects } from "./state/training/training.effects";
import { trainingFeature } from "./state/training/training.reducer";
import { UsersAccessEffects } from "./state/users-access/users-access.effects";
import { usersAccessFeature } from "./state/users-access/users-access.reducer";

/**
 * Root application providers.
 *
 * The data layer is bound here via DataRepository -> LocalStorageRepository.
 * To move to a real backend later, swap only this one useClass line for an
 * ApiRepository; no component or store code needs to change.
 *
 * New NgRx feature slices are registered in the provideStore map and their
 * effects in provideEffects — both in this single file.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(APP_ROUTES, withComponentInputBinding()),
    // An uncaught error reaches the user instead of only the console — see
    // AppErrorHandler, and standards.md §8.
    { provide: ErrorHandler, useClass: AppErrorHandler },
    provideStore({
      [accountFeature.name]: accountFeature.reducer,
      [careerPathwaysFeature.name]: careerPathwaysFeature.reducer,
      [checklistsFeature.name]: checklistsFeature.reducer,
      [companyFeature.name]: companyFeature.reducer,
      [setupFeature.name]: setupFeature.reducer,
      [divisionsFeature.name]: divisionsFeature.reducer,
      [fileDeletionsFeature.name]: fileDeletionsFeature.reducer,
      [handbookFeature.name]: handbookFeature.reducer,
      [notificationsFeature.name]: notificationsFeature.reducer,
      [onboardingFeature.name]: onboardingFeature.reducer,
      [opportunitiesFeature.name]: opportunitiesFeature.reducer,
      [packetFeature.name]: packetFeature.reducer,
      [peopleFeature.name]: peopleFeature.reducer,
      [projectSettingsFeature.name]: projectSettingsFeature.reducer,
      [projectsFeature.name]: projectsFeature.reducer,
      [referenceFeature.name]: referenceFeature.reducer,
      [rpmFeature.name]: rpmFeature.reducer,
      [scheduleFeature.name]: scheduleFeature.reducer,
      [sessionFeature.name]: sessionFeature.reducer,
      [subcontractorsFeature.name]: subcontractorsFeature.reducer,
      [timeOffFeature.name]: timeOffFeature.reducer,
      [trainingFeature.name]: trainingFeature.reducer,
      [usersAccessFeature.name]: usersAccessFeature.reducer,
    }),
    provideEffects(
      CareerPathwaysEffects,
      ChecklistsEffects,
      CompanyEffects,
      SetupEffects,
      DivisionsEffects,
      FileDeletionsEffects,
      HandbookEffects,
      OnboardingEffects,
      OpportunitiesEffects,
      PacketEffects,
      PeopleEffects,
      ProjectSettingsEffects,
      ProjectsEffects,
      ReferenceEffects,
      RpmEffects,
      ScheduleEffects,
      SubcontractorsEffects,
      TimeOffEffects,
      TrainingEffects,
      UsersAccessEffects,
    ),
    isDevMode() ? provideStoreDevtools({ maxAge: 25 }) : [],
    { provide: DataRepository, useClass: LocalStorageRepository },
  ],
};
